import React, { useState } from 'react';
import api, { getApiErrorMessage } from '../../api';
import { FileIcon, loadShareState, saveShareState } from './shareUtils';
import './Share.css';

/**
 * Участник оставляет почту, чтобы получить презентацию спикера.
 * variant: card — карточка (после ответа, на странице по QR); collapsible — свёрнутый блок у формы вопроса.
 * dismissible — кнопка «Не нужно»: после неё карточка больше не показывается в этой вкладке.
 */
function ShareCard({ kind, refId, share, variant = 'card', dismissible = false, title }) {
    const saved = loadShareState(kind, refId);
    const [state, setState] = useState(saved?.state || 'form'); // form | sending | done | dismissed
    const [doneInfo, setDoneInfo] = useState(saved?.info || null);
    const [open, setOpen] = useState(variant !== 'collapsible');
    const [email, setEmail] = useState('');
    const [consent, setConsent] = useState(false);
    const [marketing, setMarketing] = useState(false);
    const [shareEmail, setShareEmail] = useState(false);
    const [website, setWebsite] = useState(''); // ловушка для ботов
    const [error, setError] = useState('');

    if (!share?.enabled || state === 'dismissed') return null;

    const dismiss = () => {
        saveShareState(kind, refId, { state: 'dismissed' });
        setState('dismissed');
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!consent) {
            setError('Отметьте согласие на обработку почты — без него мы не сможем отправить письмо');
            return;
        }
        setState('sending');
        setError('');
        try {
            const res = await api.post('/public/presentation-requests', {
                kind, ref: refId, email: email.trim(), consent_pd: true,
                marketing, share_with_organizer: shareEmail, website: website || undefined,
            });
            const info = { email: res.data.email, waiting: res.data.status === 'waiting' };
            saveShareState(kind, refId, { state: 'done', info });
            setDoneInfo(info);
            setState('done');
        } catch (err) {
            const status = err.response?.status;
            setError(status === 422 ? 'Проверьте адрес почты' : getApiErrorMessage(err, 'Не удалось отправить. Попробуйте ещё раз.'));
            setState('form');
        }
    };

    if (state === 'done') {
        return (
            <div className={`share-card share-card--done share-card--${variant}`} role="status">
                <FileIcon />
                <div>
                    <b>{doneInfo?.waiting ? 'Пришлём, когда спикер загрузит презентацию' : 'Презентация отправлена'}</b>
                    <span>
                        {doneInfo?.waiting
                            ? <>Письмо придёт на {doneInfo?.email} сразу после загрузки.</>
                            : <>Письмо придёт на {doneInfo?.email} в течение нескольких минут. Если его нет — проверьте «Спам».</>}
                    </span>
                </div>
            </div>
        );
    }

    if (!open) {
        return (
            <button type="button" className="share-toggle" onClick={() => setOpen(true)} aria-expanded="false">
                <FileIcon /> <span>Получить презентацию спикера на почту</span> <i aria-hidden>›</i>
            </button>
        );
    }

    return (
        <form className={`share-card share-card--${variant}`} onSubmit={submit} noValidate>
            <div className="share-card__head">
                <FileIcon />
                <div>
                    <h3>{title || 'Получить презентацию'}</h3>
                    <p>
                        {share.ready
                            ? 'Пришлём ссылку на презентацию спикера на вашу почту.'
                            : 'Спикер ещё не загрузил презентацию — оставьте почту, и мы пришлём её, как только она появится.'}
                    </p>
                </div>
                {variant === 'collapsible' && (
                    <button type="button" className="share-card__close" onClick={() => setOpen(false)} aria-label="Свернуть">×</button>
                )}
            </div>
            <label className="share-card__email">
                <span>Для получения презентации введите email</span>
                <input
                    type="email" inputMode="email" autoComplete="email" required maxLength={254}
                    placeholder="name@mail.ru"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                />
            </label>
            <input
                className="share-card__trap" type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
                value={website} onChange={(e) => setWebsite(e.target.value)}
            />
            <label className="share-check">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                <span>Согласен(на) на обработку моего email, чтобы получить презентацию. <a href="/terms" target="_blank" rel="noreferrer">Условия</a></span>
            </label>
            <label className="share-check">
                <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} />
                <span>Хочу получать новости и предложения СВОД — отписаться можно в любом письме</span>
            </label>
            <label className="share-check">
                <input type="checkbox" checked={shareEmail} onChange={(e) => setShareEmail(e.target.checked)} />
                <span>Передать мой email организатору мероприятия</span>
            </label>
            {error && <p className="share-card__error" role="alert">{error}</p>}
            <div className="share-card__actions">
                <button type="submit" className="btn btn--primary" disabled={state === 'sending' || !email.trim()}>
                    {state === 'sending' ? 'Отправляем…' : 'Получить презентацию'}
                </button>
                {dismissible && <button type="button" className="share-card__skip" onClick={dismiss}>Не нужно</button>}
            </div>
        </form>
    );
}

export default ShareCard;
